import { useEffect, useState } from "react";
import { decryptData, defaulUrl, deleter, fmt } from "../components/const";
import ModalEditeAvanceFournisseur from "./ModalEditeAvanceFournisseur";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const RapportComptable = () => {
    const today = new Date().toISOString().slice(0, 10);
    const [dateStart, setDateStart] = useState(today);
    const [dateEnd, setDateEnd] = useState(today);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const [firstLoad, setFirstLoad] = useState(true);
    const [totals, setTotals] = useState({
        versement: 0,
        remboursement: 0,
        entree: 0,
        depense: 0,
        sortie: 0,
        salaire: 0,
        avanceFourn: 0,
    });
    const [search, setSearch] = useState('');
    const [selectedAvance, setSelectedAvance] = useState(null);
    const user = decryptData("USER");

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${defaulUrl}comptable/rapport/${dateStart}/${dateEnd}`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${decryptData("ACCESS_TOKEN")}`,
                },
            });
            const result = await response.json();
            if (response.status === 200) {
                setSearch('');
                setData(result);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des données :", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (firstLoad) {
            fetchData();
            setFirstLoad(false);
        }
    }, [firstLoad]);

    useEffect(() => {
        if (!data) return;
        const calcTotal = (items, field) => items?.reduce((acc, item) => acc + (item[field] || 0), 0);
        setTotals({
            versement: calcTotal(data.encaissements, "montant"),
            remboursement: calcTotal(data.remboursements, "montant"),
            entree: calcTotal(data.entree, "montant"),
            depense: calcTotal(data.depenses, "depense"),
            sortie: calcTotal(data.sorties, "montant"),
            salaire: calcTotal(data.salaires, "montant"),
            avanceFourn: calcTotal(data.avance_fourn, "montant_total"),
        });
    }, [data]);

    const renderTable = (title, items, headers, rows, totalField = null, isDanger = false) => {
        const filteredTotal = totalField
            ? items?.reduce((acc, item) => acc + (item[totalField] || 0), 0)
            : null;

        return (
            <>
                <h5 className="p-2 fw-bold text-primary">{title}</h5>
                {items?.length ? (
                    <table className={`table table-striped ${isDanger ? "table-danger" : "table-success"}`}>
                        <thead>
                            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows}
                            {filteredTotal !== null && (
                                <tr className="fw-bold bg-secondary-subtle">
                                    <td colSpan={headers.length - 2}>TOTAL</td>
                                    <td>{filteredTotal?.toLocaleString()} GNF</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div className="row justify-content-center mx-3 fs-3 bg-danger-subtle text-danger">
                        Aucune donnée trouvée
                    </div>
                )}
            </>
        );
    };

    const exportRapportPDF = () => {
        if (!data) return;
        const doc = new jsPDF("l", "pt");
        const pageWidth = doc.internal.pageSize.getWidth();
        let startY = 20;

        const addSection = (title, columns, rows, total, totalLabel) => {
            if (!rows || rows.length === 0) return;
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 128);
            doc.text(title, 20, startY);
            startY += 10;

            autoTable(doc, {
                startY,
                head: [columns.map(c => c.header)],
                body: rows.map(r => columns.map(c => r[c.dataKey])),
                foot: total ? [[{ content: totalLabel, colSpan: columns.length - 1, styles: { halign: 'right' } }, fmt(total) + " GNF"]] : [],
                theme: "grid",
                styles: { fontSize: 9 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
                footStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
                didDrawPage: () => {
                    const pageNumber = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(`Page ${pageNumber}`, pageWidth - 40, doc.internal.pageSize.getHeight() - 10, { align: "right" });
                }
            });
            startY = doc.lastAutoTable.finalY + 20;
        };

        const bilanRows = [
            ["TOTAL ENCAISSEMENT", fmt(totals.versement) + " GNF"],
            ["TOTAL REMBOURSEMENT", fmt(totals.remboursement) + " GNF"],
            ["TOTAL ENTREE", fmt(totals.entree) + " GNF"],
            ["TOTAL DEPENSE", fmt(totals.depense) + " GNF"],
            ["TOTAL SORTIE", fmt(totals.sortie) + " GNF"],
            ["TOTAL SALAIRE", fmt(totals.salaire) + " GNF"],
            ["TOTAL AVANCE FOURNISSEUR", fmt(totals.avanceFourn) + " GNF"],
        ];

        autoTable(doc, {
            startY,
            head: [["BILAN", "MONTANT"]],
            body: bilanRows,
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
        });
        startY = doc.lastAutoTable.finalY + 20;

        addSection(
            "Encaissements Magasins",
            [{ header: "N°", dataKey: "index" }, { header: "Magasin", dataKey: "magasin" }, { header: "Montant", dataKey: "montant" }, { header: "Date", dataKey: "created_at" }],
            data.encaissements?.map((e, i) => ({ index: i + 1, magasin: e.magasin, montant: fmt(e.montant), created_at: e.created_at })),
            totals.versement,
            "TOTAL"
        );

        addSection(
            "Entrées de caisse",
            [{ header: "N°", dataKey: "index" }, { header: "Description", dataKey: "description" }, { header: "Montant", dataKey: "montant" }, { header: "Type", dataKey: "type" }, { header: "Date", dataKey: "created_at" }],
            data.entree?.map((e, i) => ({ index: i + 1, description: e.description, montant: fmt(e.montant), type: e.type, created_at: e.created_at })),
            totals.entree,
            "TOTAL"
        );

        addSection(
            "Dépenses sur conteneurs",
            [{ header: "N° Cont.", dataKey: "numero" }, { header: "Montant", dataKey: "depense" }, { header: "Conteneur", dataKey: "conteneur" }, { header: "Description", dataKey: "description" }, { header: "Date", dataKey: "created_at" }],
            data.depenses?.map((d) => ({ numero: d.numero, depense: fmt(d.depense), conteneur: d.conteneur, description: d.description, created_at: d.created_at })),
            totals.depense,
            "TOTAL"
        );

        addSection(
            "Remboursements Fournisseurs",
            [{ header: "N°", dataKey: "index" }, { header: "Fournisseur", dataKey: "fournisseur" }, { header: "Montant", dataKey: "montant" }, { header: "Mode Paiement", dataKey: "source" }, { header: "N° Facture", dataKey: "numero" }, { header: "Date", dataKey: "created_at" }],
            data.remboursements?.map((r, i) => ({ index: i + 1, fournisseur: r.fournisseur, montant: fmt(r.montant), source: r.source, numero: r.numero, created_at: r.created_at })),
            totals.remboursement,
            "TOTAL"
        );

        addSection(
            "Avances Fournisseurs",
            [{ header: "N°", dataKey: "index" }, { header: "Fournisseur", dataKey: "fournisseur" }, { header: "Montant", dataKey: "montant_total" }, { header: "Mode Paiement", dataKey: "source" }, { header: "Description", dataKey: "descriptions" }, { header: "Date", dataKey: "created_at" }],
            data.avance_fourn?.map((a, i) => ({ index: i + 1, fournisseur: a.fournisseur, montant_total: fmt(a.montant_total), source: a.source, descriptions: a.descriptions, created_at: a.created_at })),
            totals.avanceFourn,
            "TOTAL"
        );

        addSection(
            "Sorties et Versements Bancaires",
            [{ header: "N°", dataKey: "index" }, { header: "Description", dataKey: "description" }, { header: "Montant", dataKey: "montant" }, { header: "Type", dataKey: "type" }, { header: "Date", dataKey: "created_at" }],
            data.sorties?.filter(s => s.type !== "entree").map((s, i) => ({ index: i + 1, description: s.description, montant: fmt(s.montant), type: s.type, created_at: s.created_at })),
            totals.sortie,
            "TOTAL"
        );

        addSection(
            "Salaires et Avances sur Salaires",
            [{ header: "N°", dataKey: "index" }, { header: "Nom", dataKey: "nom" }, { header: "Téléphone", dataKey: "phone" }, { header: "Montant", dataKey: "montant" }, { header: "Type", dataKey: "type" }, { header: "Date", dataKey: "created_at" }],
            data.salaires?.map((s, i) => ({ index: i + 1, nom: s.nom, phone: s.phone, montant: fmt(s.montant), type: s.type, created_at: s.created_at })),
            totals.salaire,
            "TOTAL"
        );

        doc.save("Rapport_Comptable.pdf");
    };

    return (
        <div className=" ">
            <div className="row mb-2 p-3 bg-secondary-subtle justify-content-between">
                <div className="col-lg-6">
                    <h4 className="text-primary">FANSAN TEXTIL – COMPTABILITÉ</h4>
                </div>
                <div className="col-lg-6 d-flex align-items-center justify-content-end">
                    <input type="date" className="form-control m-1 w-auto" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
                    <input type="date" className="form-control m-1 w-auto" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
                    <button className="btn btn-primary m-1 btn-sm" onClick={fetchData}><i className="bx bx-search"></i></button>
                    <button className="btn btn-primary btn-sm w-auto" onClick={() => exportRapportPDF()}>
                        <i className="bx bx-printer"></i> pdf
                    </button>
                </div>
            </div>

            {(isLoading || !data) ? (
                <div className="loader"></div>
            ) : (
                <div className="table-responsive">
                    <input type="search" className="form-control" placeholder="Rechercher..." onChange={(e) => setSearch(e.target.value)} />

                    {renderTable(
                        "Encaissements Magasins",
                        data.encaissements?.filter(ec => ec.magasin?.toLowerCase().includes(search.toLowerCase())),
                        ["N°", "Magasin", "Montant", "Date"],
                        data.encaissements?.filter(ec => ec.magasin?.toLowerCase().includes(search.toLowerCase()))?.map((e, i) => (
                            <tr key={i}><td>{i + 1}</td><td>{e.magasin}</td><td>{e.montant?.toLocaleString()}</td><td>{e.created_at}</td></tr>
                        )),
                        "montant"
                    )}

                    {renderTable(
                        "Entrées de caisse",
                        data.entree?.filter(et => et.description?.toLowerCase().includes(search.toLowerCase())),
                        ["N°", "Description", "Montant", "Type", "Date"],
                        data.entree?.filter(et => et.description?.toLowerCase().includes(search.toLowerCase()))?.map((e, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{e.description}</td>
                                <td>{e.montant?.toLocaleString()}</td>
                                <td>{e.type}</td>
                                <td>{e.created_at}</td>
                            </tr>
                        )),
                        "montant"
                    )}

                    {renderTable(
                        "Dépenses sur conteneurs",
                        data.depenses?.filter(d => d.conteneur?.toLowerCase().includes(search.toLowerCase())),
                        ["N° Cont.", "Montant", "Conteneur", "Description", "Date"],
                        data.depenses?.filter(d => d.conteneur?.toLowerCase().includes(search.toLowerCase()))?.map((d, i) => (
                            <tr key={i}><td>{d.numero}</td><td>{d.depense?.toLocaleString()}</td><td>{d.conteneur}</td><td>{d.description}</td><td>{d.created_at}</td></tr>
                        )),
                        "depense",
                        true
                    )}

                    {renderTable(
                        "Remboursements Fournisseurs",
                        data.remboursements?.filter(f => f.fournisseur?.toLowerCase().includes(search.toLowerCase())),
                        ["N°", "Fournisseur", "Montant", "Mode Paiement", "N° Facture", "Date"],
                        data.remboursements?.filter(f => f.fournisseur?.toLowerCase().includes(search.toLowerCase()))?.map((r, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.fournisseur}</td>
                                <td>{r.montant?.toLocaleString()}</td>
                                <td>{r.source}</td>
                                <td>{r.numero}</td>
                                <td>{r.created_at}</td>
                            </tr>
                        )),
                        "montant"
                    )}

                    {renderTable(
                        "Avances Fournisseurs",
                        data.avance_fourn?.filter(f => f.fournisseur?.toLowerCase().includes(search.toLowerCase())),
                        ["N°", "Fournisseur", "Montant", "Mode Paiement", "Description", "Date"],
                        data.avance_fourn?.filter(f => f.fournisseur?.toLowerCase().includes(search.toLowerCase()))?.map((a, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{a.fournisseur}</td>
                                <td>{a.montant_total?.toLocaleString()}</td>
                                <td>{a.source}</td>
                                <td>{a.descriptions}</td>
                                <td>{a.created_at}</td>
                            </tr>
                        )),
                        "montant_total"
                    )}

                    {renderTable(
                        "Sorties et Versements Bancaires",
                        data.sorties?.filter(s => s.description?.toLowerCase().includes(search.toLowerCase()) && s.type !== "entree"),
                        ["N°", "Description", "Montant", "Type", "Date"],
                        data.sorties?.filter(s => s.description?.toLowerCase().includes(search.toLowerCase()) && s.type !== "entree")?.map((s, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{s.description}</td>
                                <td>{s.montant?.toLocaleString()} GNF</td>
                                <td>{s.type}</td>
                                <td>{s.created_at}</td>
                            </tr>
                        )),
                        "montant"
                    )}

                    {renderTable(
                        "Salaires et Avances sur Salaires",
                        data.salaires?.filter(f => f.nom?.toLowerCase().includes(search.toLowerCase())),
                        ["N°", "Nom", "Téléphone", "Montant", "Type", "Date"],
                        data.salaires?.filter(f => f.nom?.toLowerCase().includes(search.toLowerCase()))?.map((s, i) => (
                            <tr key={i} className={s.type === "avance" ? "bg-info-subtle" : ""}>
                                <td>{i + 1}</td>
                                <td>{s.nom}</td>
                                <td>{s.phone}</td>
                                <td>{s.montant?.toLocaleString()} GNF</td>
                                <td>{s.type}</td>
                                <td>{s.created_at}</td>
                            </tr>
                        )),
                        "montant"
                    )}
                </div>
            )}

            {selectedAvance && (
                <ModalEditeAvanceFournisseur
                    show={!!selectedAvance}
                    onClose={() => setSelectedAvance(null)}
                    data={selectedAvance}
                    onSave={fetchData}
                />
            )}
        </div>
    );
};

export default RapportComptable;
